// JOBE-0160.3: W1 Risk heat map — departments × risk axes with cell drill.
//
// Renders the rows×cols×cells matrix into <HeatMap />. Cell click navigates
// to /department/{slug}?risk={axis} with drill context. The signal cell is
// the highest-scoring cell.
//
// Data source — 2026-05-24:
//   When wcc.mock.heatmap is ON, the flat fixture (5 depts × 3 risk axes:
//   accuracy / latency / compliance) is rendered as-is.
//   When OFF (default), the matrix is derived from useOrganizationPeople():
//     rows = unique dept slugs (alphabetical)
//     cols = ['healthy', 'degraded', 'disabled'] — agent-phase buckets
//     score = bucketCount / totalAgents (per-dept normalized share)
//   "Disabled" is the headline risk axis (so the highest disabled share
//   becomes the signal cell). True accuracy/latency/compliance scores need
//   metric endpoints that don't exist on the wire yet.

import { useNavigate } from 'react-router-dom';
import { flaggedFetch } from '../lib/flags.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { HeatMap } from '../components/HeatMap/HeatMap.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { useOrganizationPeople } from '../organization/fetchOrganizationPeople.js';

// Fixture axes (mock branch only).
const MOCK_DEPTS = ['Finance', 'Legal', 'Platform', 'Research', 'Home Ops'];
const MOCK_AXES  = ['accuracy', 'latency', 'compliance'];

// Derived axes (real-data branch). Disabled is the risk signal — agents
// not running are the loudest operator concern.
const DERIVED_AXES = ['healthy', 'degraded', 'disabled'];

// Build a 2D array [rows][cols] of scores from the flat cell list.
// Missing cells get score 0 (no-data treated as lowest heat).
function buildCellMatrix(cells, rows, cols) {
  const matrix = rows.map(() => cols.map(() => null));
  for (const cell of cells) {
    const r = rows.indexOf(cell.dept);
    const c = cols.indexOf(cell.axis);
    if (r >= 0 && c >= 0) {
      matrix[r][c] = cell.score;
    }
  }
  return matrix;
}

// Find the [rowIndex, colIndex] of the cell with the highest score.
// Returns null if no valid cells.
function findSignalCell(cells, rows, cols) {
  let bestScore = -Infinity;
  let bestR = -1;
  let bestC = -1;
  for (const cell of cells) {
    if (typeof cell.score !== 'number') continue;
    const r = rows.indexOf(cell.dept);
    const c = cols.indexOf(cell.axis);
    if (r >= 0 && c >= 0 && cell.score > bestScore) {
      bestScore = cell.score;
      bestR = r;
      bestC = c;
    }
  }
  if (bestR < 0) return null;
  return [bestR, bestC];
}

// Derive the heatmap inputs from the org directory.
// Returns { rows, cols, cells, source } with the same shape mock-mode uses.
function deriveFromOrg(orgRows) {
  const agents = orgRows.filter((r) => r.type === 'agent');
  // Group agents by dept slug. Use slug (not display name) as row label —
  // it's what the click drill expects and is already lowercased.
  const byDept = new Map();
  for (const a of agents) {
    const dept = a.dept || 'unassigned';
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept).push(a);
  }
  const deptRows = Array.from(byDept.keys()).sort();
  const flatCells = [];
  for (const dept of deptRows) {
    const members = byDept.get(dept);
    const total = members.length;
    if (total === 0) continue;
    const healthy = members.filter((m) => m.phase === 'Available').length;
    const degraded = members.filter((m) => m.phase === 'Degraded').length;
    const disabled = members.filter(
      (m) => m.phase === 'Disabled' || m.phase == null,
    ).length;
    flatCells.push({ dept, axis: 'healthy',  score: healthy  / total });
    flatCells.push({ dept, axis: 'degraded', score: degraded / total });
    flatCells.push({ dept, axis: 'disabled', score: disabled / total });
  }
  return { rows: deptRows, cols: DERIVED_AXES, cells: flatCells };
}

export function RiskHeatMap() {
  const navigate = useNavigate();
  const overrides = useFlagStore((s) => s.overrides);
  const mockHeatmap = flaggedFetch('wcc.mock.heatmap', overrides);
  const { rows: orgRows } = useOrganizationPeople();

  let rows, cols, cells, source;
  if (mockHeatmap) {
    rows = MOCK_DEPTS;
    cols = MOCK_AXES;
    cells = mockHeatmap.cells || [];
    source = 'wcc.mock.heatmap';
  } else {
    const derived = deriveFromOrg(orgRows);
    rows = derived.rows;
    cols = derived.cols;
    cells = derived.cells;
    source = 'wcc-heatmap';
  }

  // No agents derived → nothing to render (org fetch still loading or empty).
  if (!rows.length) return null;

  // Fill any missing cells with score 0 for the matrix.
  const filledCells = [];
  for (const dept of rows) {
    for (const axis of cols) {
      const existing = cells.find((c) => c.dept === dept && c.axis === axis);
      filledCells.push(existing || { dept, axis, score: 0, agentCount: 0 });
    }
  }
  const matrix = buildCellMatrix(filledCells, rows, cols);
  const safeMatrix = matrix.map((row) => row.map((v) => (v === null ? 0 : v)));
  const signalCell = findSignalCell(cells, rows, cols);

  const handleCellClick = (rowLabel, colLabel, score) => {
    const deptSlug = rowLabel.toLowerCase().replace(/\s+/g, '-');
    const targetUrl = `/department/${deptSlug}?risk=${colLabel}`;
    const ctx = {
      source,
      dept: rowLabel,
      axis: colLabel,
      score,
    };
    navigate(withDrillContext(targetUrl, ctx));
  };

  const ariaLabel = mockHeatmap
    ? 'Workforce risk heat map — 5 departments × 3 risk axes'
    : `Workforce risk heat map — ${rows.length} departments × ${cols.length} phase buckets`;

  return (
    <HeatMap
      rows={rows}
      cols={cols}
      cells={safeMatrix}
      onCellClick={handleCellClick}
      signalCell={signalCell}
      ariaLabel={ariaLabel}
    />
  );
}
